import { useEffect, useState } from 'react';
import axios from 'axios';

export interface PhonePattern {
  pattern: string;
  len: number;
  formatted_number: string;
}

export interface CountryPhoneInfo {
  country: string;
  country_code: string;
  phone_code: string;
  phone_pattern: PhonePattern;
  country_flag: string;
}

interface UsePhoneFormatProps {
  countryCode: string; // e.g., "US", "MX"
  phoneNumber: string;
}

interface UsePhoneFormatReturn {
  formattedPhoneNumber: string;
  fullPhoneNumber: string;
  isValid: boolean;
  phonePattern: PhonePattern | null;
  isLoading: boolean;
  error: string | null;
  countryData: CountryPhoneInfo | null;
}

// Cache for country data to avoid repeated API calls
const countryCache: Record<string, CountryPhoneInfo> = {};

export const usePhoneFormat = ({ countryCode, phoneNumber }: UsePhoneFormatProps): UsePhoneFormatReturn => {
  const [countryData, setCountryData] = useState<CountryPhoneInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState('');
  const [fullPhoneNumber, setFullPhoneNumber] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [allCountries, setAllCountries] = useState<CountryPhoneInfo[]>([]);

  // Fetch all countries data once
  useEffect(() => {
    const fetchAllCountries = async () => {
      if (allCountries.length > 0) return;

      setIsLoading(true);
      setError(null);
      
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/country`);
        if (response.data && response.data.data) {
          const countriesData = response.data.data as CountryPhoneInfo[];
          setAllCountries(countriesData);
          
          // Cache all countries for faster lookup
          countriesData.forEach(country => {
            countryCache[country.country_code] = country;
          });
        }
      } catch (err) {
        setError('Failed to fetch countries data');
        console.error('Error fetching countries data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllCountries();
  }, []);

  // Update country data when country code changes
  useEffect(() => {
    if (!countryCode || allCountries.length === 0) return;
    
    setIsLoading(true);
    setError(null);
    
    // Check cache first
    if (countryCache[countryCode]) {
      setCountryData(countryCache[countryCode]);
      setIsLoading(false);
      return;
    }
    
    // If not in cache, find in our allCountries data
    const country = allCountries.find(c => c.country_code === countryCode);
    
    if (country) {
      setCountryData(country);
      countryCache[countryCode] = country; // Update cache
    } else {
      setError(`Country with code ${countryCode} not found`);
    }
    
    setIsLoading(false);
  }, [countryCode, allCountries]);

  // Format phone number based on country pattern
  useEffect(() => {
    if (!countryData || !phoneNumber) {
      setFormattedPhoneNumber('');
      setFullPhoneNumber('');
      setIsValid(false);
      return;
    }

    const { phone_pattern, phone_code } = countryData;
    
    // Remove all non-numeric characters
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    
    // Format phone number based on country pattern
    let formatted = '';
    
    if (phone_pattern) {
      const { pattern, len } = phone_pattern;
      
      // Check if the phone number is valid according to the country's expected length
      setIsValid(digitsOnly.length === len);
      
      if (pattern.includes('X')) {
        let digitIndex = 0;
        
        // Apply the pattern (X represents a digit)
        for (let i = 0; i < pattern.length; i++) {
          if (pattern[i] === 'X') {
            if (digitIndex < digitsOnly.length) {
              formatted += digitsOnly[digitIndex];
              digitIndex++;
            } else {
              formatted += '_'; // Placeholder for missing digits
            }
          } else {
            formatted += pattern[i];
          }
        }
      } else {
        // If pattern doesn't have X markers, just use the digits
        formatted = digitsOnly;
      }
    } else {
      formatted = digitsOnly;
      setIsValid(digitsOnly.length >= 7); // Default validation
    }
    
    setFormattedPhoneNumber(formatted);
    
    // Create full phone number with country code
    if (phone_code) {
      setFullPhoneNumber(`${phone_code} ${formatted}`);
    } else {
      setFullPhoneNumber(formatted);
    }
  }, [countryData, phoneNumber]);

  return {
    formattedPhoneNumber,
    fullPhoneNumber,
    isValid,
    phonePattern: countryData?.phone_pattern || null,
    isLoading,
    error,
    countryData
  };
}; 